import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import React from 'react';

interface AddonOption {
  value: string;
  label: string;
  price_cents: number;
}

export interface AddonConfig {
  id: string;
  label: string;
  icon?: string;
  description?: string;
  options: AddonOption[];
}

interface ServiceAddonSelectorProps {
  addons: AddonConfig[];
  selectedAddons: Record<string, string>;
  onChange: (addonId: string, value: string) => void;
}

const formatPrice = (cents: number) => {
  const dollars = (cents || 0) / 100;
  return `$${dollars.toLocaleString('en-US')}/mo`;
};

const getSelectedOption = (addon: AddonConfig, selectedValue: string | undefined) => {
  if (!Array.isArray(addon.options) || addon.options.length === 0) {
    return null;
  }

  const normalized = selectedValue || 'none';
  return addon.options.find((option) => option.value === normalized) || addon.options[0];
};

const sortedOptions = (options: AddonOption[]) => {
  const none = options.find((opt) => opt.value === 'none');
  const rest = options.filter((opt) => opt.value !== 'none');
  return none ? [none, ...rest] : options;
};

const ServiceAddonSelector: React.FC<ServiceAddonSelectorProps> = ({
  addons,
  selectedAddons,
  onChange,
}) => {
  if (!addons?.length) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Optional Add-ons
      </Typography>

      {addons.map((addon) => {
        const options = sortedOptions(addon.options || []);
        const selected = getSelectedOption(addon, selectedAddons?.[addon.id]);

        if (!options.length || !selected) return null;

        return (
          <Paper
            key={addon.id}
            variant="outlined"
            sx={{ mb: 1.5, borderRadius: 2, borderColor: 'divider', overflow: 'hidden' }}
          >
            <Accordion disableGutters elevation={0} defaultExpanded={false} sx={{ '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {addon.icon ? `${addon.icon} ` : ''}
                    {addon.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {selected.price_cents > 0 ? `${selected.label} +${formatPrice(selected.price_cents)}` : 'None'}
                  </Typography>
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                {addon.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {addon.description}
                  </Typography>
                )}

                <RadioGroup
                  value={selectedAddons?.[addon.id] || 'none'}
                  onChange={(event) => onChange(addon.id, event.target.value)}
                >
                  {options.map((option) => (
                    <Box
                      key={option.value}
                      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.25 }}
                    >
                      <FormControlLabel
                        value={option.value}
                        control={<Radio size="small" />}
                        label={<Typography variant="body2">{option.label}</Typography>}
                        sx={{ mr: 1 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {option.price_cents > 0 ? `+${formatPrice(option.price_cents)}` : '$0'}
                      </Typography>
                    </Box>
                  ))}
                </RadioGroup>
              </AccordionDetails>
            </Accordion>
          </Paper>
        );
      })}
    </Box>
  );
};

export default React.memo(ServiceAddonSelector);
